import { axiosInstance } from "@/lib/axios";
import queryClient from "@/lib/queryClient";
import { blogSchema, BlogType, BlogUpdateSchema } from "@/schemas/blog-schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

export const BlogQuerySchema = z.object({
  published: z.boolean().optional(),
  authorId: z.string().optional(),
  limit: z.number().min(1).max(100).optional(),
  offset: z.number().min(0).optional(),
});

export type BlogCreateType = z.infer<typeof blogSchema>;
export type BlogUpdateType = z.infer<typeof BlogUpdateSchema>;
export type BlogQueryType = z.infer<typeof BlogQuerySchema>;

// API functions
export async function getBlogsFn(query: BlogQueryType = {}) {
  const params = new URLSearchParams();
  
  if (query.published !== undefined) params.append('published', query.published.toString());
  if (query.authorId) params.append('authorId', query.authorId);
  if (query.limit) params.append('limit', query.limit.toString());
  if (query.offset) params.append('offset', query.offset.toString());
  
  const url = `/api/blogs${params.toString() ? `?${params.toString()}` : ''}`;
  return (await axiosInstance.get<{data: BlogType[], pagination: {total: number, limit: number, offset: number, hasMore: boolean}}>(url)).data;
}

export async function getBlogByIdFn(id: string) {
    return (await axiosInstance.get<BlogType>(`/api/blogs/${id}`)).data;
}

export async function createBlogFn(data: BlogCreateType) {
  return (await axiosInstance.post<BlogType>("/api/blogs", data)).data;
}

export async function updateBlogFn(data: BlogUpdateType) {
  const { id, ...updateData } = data;
  return (await axiosInstance.put(`/api/blogs/${id}`, updateData)).data;
}

export async function deleteBlogFn(id: string) {
  return (await axiosInstance.delete(`/api/blogs/${id}`)).data;
}

export async function toggleBlogPublishFn(id: string, published: boolean) {
  return (await axiosInstance.patch(`/api/blogs/${id}`, { published })).data;
}

const Blog = {
  Get: {
    useQuery: (query: BlogQueryType = {}, options = {}) =>
      useQuery({
        queryKey: ["Blogs", query],
        queryFn: () => getBlogsFn(query),
        ...options,
      }),
  },

  GetById: {
    useQuery: (id: string, options = {}) =>
      useQuery({
        queryKey: ["Blog", id],
        queryFn: () => getBlogByIdFn(id),
        enabled: !!id,
        ...options,
      }),
  },

  Create: {
    useMutation: (options = {}) => {
      let toastId: string | number | null = null;
      return useMutation({
        mutationFn: createBlogFn,
        onSuccess: (data) => {
          toastId && toast.dismiss(toastId);
          toast.success(`Blog "${data.title}" created successfully`);
          queryClient.invalidateQueries({ queryKey: ["Blogs"] });
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error || "Failed to create blog";
          toast.error(message);
        },
        onMutate: () => {
          toastId = toast.loading("Creating blog...");
        },
        ...options,
      });
    },
  },

  Update: {
    useMutation: (options = {}) => {
      let toastId: string | number | null = null;
      return useMutation({
        mutationFn: updateBlogFn,
        onSuccess: (data) => {
          toastId && toast.dismiss(toastId);
          toast.success(`Blog "${data.title}" updated successfully`);
          queryClient.invalidateQueries({ queryKey: ["Blogs"] });
          queryClient.invalidateQueries({ queryKey: ["Blog", data.id] });
        },
        onError: (error: any) => {
          console.error("err", error);
          const message =
            error?.response?.data?.error || "Failed to update blog";
          toast.error(message);
        },
        onMutate: () => {
          toastId = toast.loading("Updating blog...");
        },
        ...options,
      });
    },
  },

  Delete: {
    useMutation: (options = {}) =>
      useMutation({
        mutationFn: deleteBlogFn,
        onSuccess: () => {
          toast.success("Blog deleted successfully");
          queryClient.invalidateQueries({ queryKey: ["Blogs"] });
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error || "Failed to delete blog";
          toast.error(message);
        },
        onMutate: () => toast.loading("Deleting blog..."),
        ...options,
      }),
  },

  TogglePublish: {
    useMutation: (options = {}) =>
      useMutation({
        mutationFn: ({ id, published }: { id: string; published: boolean }) =>
          toggleBlogPublishFn(id, published),
        onSuccess: (data, variables) => {
          const status = variables.published ? "published" : "unpublished";
          toast.success(`Blog ${status} successfully`);
          queryClient.invalidateQueries({ queryKey: ["Blogs"] });
          queryClient.invalidateQueries({ queryKey: ["Blog", variables.id] });
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error || "Failed to update blog status";
          toast.error(message);
        },
        onMutate: (variables) => {
          const action = variables.published ? "Publishing" : "Unpublishing";
          toast.loading(`${action} blog...`);
        },
        ...options,
      }),
  },
};

export default Blog;